(function () {
  'use strict';

  if (customElements.get('header-menu-rail')) return;

  class HeaderMenuRail extends HTMLElement {
    constructor() {
      super();
      this.offset = 0;
      this.maximumOffset = 0;
      this.autoScrollDelay = 5000;
      this.autoScrollDirection = 1;
      this.autoScrollTimer = null;
      this.pointerInside = false;
      this.focusInside = false;
      this.userControlled = false;
      this.resizeObserver = null;
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.handlePrevious = this.scrollPrevious.bind(this);
      this.handleNext = this.scrollNext.bind(this);
      this.handleFocusIn = this.handleRailFocus.bind(this);
      this.handleFocusEnter = this.pauseForFocus.bind(this);
      this.handleFocusLeave = this.resumeAfterFocus.bind(this);
      this.handlePointerEnter = this.pauseForPointer.bind(this);
      this.handlePointerLeave = this.resumeAfterPointer.bind(this);
      this.handleTrackPointerDown = this.stopAutoScroll.bind(this);
      this.handleReducedMotionChange = this.syncAutoScroll.bind(this);
      this.handleTransitionEnd = this.finishTransition.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;

      this.viewport = this.querySelector('[data-nav-menu-viewport]');
      this.track = this.querySelector('[data-nav-menu-track]');
      this.previousButton = this.querySelector('[data-nav-menu-previous]');
      this.nextButton = this.querySelector('[data-nav-menu-next]');
      if (!this.viewport || !this.track || !this.previousButton || !this.nextButton) return;

      this.initialized = true;
      this.previousButton.addEventListener('click', this.handlePrevious);
      this.nextButton.addEventListener('click', this.handleNext);
      this.track.addEventListener('focusin', this.handleFocusIn);
      this.track.addEventListener('pointerdown', this.handleTrackPointerDown);
      this.track.addEventListener('transitionend', this.handleTransitionEnd);
      this.addEventListener('focusin', this.handleFocusEnter);
      this.addEventListener('focusout', this.handleFocusLeave);
      this.addEventListener('mouseenter', this.handlePointerEnter);
      this.addEventListener('mouseleave', this.handlePointerLeave);
      this.reducedMotionQuery.addEventListener('change', this.handleReducedMotionChange);

      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this);
      this.resizeObserver.observe(this.viewport);
      this.resizeObserver.observe(this.track);

      this.measure();
      document.fonts?.ready.then(() => {
        if (this.isConnected) this.measure();
      });
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      this.previousButton.removeEventListener('click', this.handlePrevious);
      this.nextButton.removeEventListener('click', this.handleNext);
      this.track.removeEventListener('focusin', this.handleFocusIn);
      this.track.removeEventListener('pointerdown', this.handleTrackPointerDown);
      this.track.removeEventListener('transitionend', this.handleTransitionEnd);
      this.removeEventListener('focusin', this.handleFocusEnter);
      this.removeEventListener('focusout', this.handleFocusLeave);
      this.removeEventListener('mouseenter', this.handlePointerEnter);
      this.removeEventListener('mouseleave', this.handlePointerLeave);
      this.reducedMotionQuery.removeEventListener('change', this.handleReducedMotionChange);
      this.clearAutoScroll();
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      this.initialized = false;
    }

    get isRtl() {
      return getComputedStyle(this).direction === 'rtl';
    }

    measure() {
      const trackWidth = Math.ceil(this.track.getBoundingClientRect().width);
      const viewportWidth = Math.floor(this.viewport.getBoundingClientRect().width);
      this.maximumOffset = Math.max(0, trackWidth - viewportWidth);

      const hasOverflow = this.maximumOffset > 1;
      this.toggleAttribute('data-overflow', hasOverflow);
      this.previousButton.hidden = !hasOverflow;
      this.nextButton.hidden = !hasOverflow;

      this.setOffset(hasOverflow ? Math.min(this.offset, this.maximumOffset) : 0, false);
      this.syncAutoScroll();
    }

    setOffset(nextOffset, animate) {
      this.offset = Math.max(0, Math.min(nextOffset, this.maximumOffset));
      const direction = this.isRtl ? 1 : -1;
      const shouldAnimate = animate && !this.reducedMotionQuery.matches;
      this.track.classList.toggle('nav-links--animating', shouldAnimate);
      if (this.offset <= 1) {
        this.track.style.removeProperty('transform');
      } else {
        this.track.style.transform = `translate3d(${direction * this.offset}px, 0, 0)`;
      }
      this.updateControls();
    }

    scrollPrevious() {
      this.stopAutoScroll();
      this.setOffset(this.offset - this.getScrollDistance(), true);
    }

    scrollNext() {
      this.stopAutoScroll();
      this.setOffset(this.offset + this.getScrollDistance(), true);
    }

    getScrollDistance() {
      return Math.max(120, Math.round(this.viewport.clientWidth * 0.6));
    }

    handleRailFocus(event) {
      const item = event.target.closest('.nav-links__item');
      if (!item || !this.track.contains(item)) return;

      const viewportRect = this.viewport.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const startOverflow = this.isRtl
        ? itemRect.right - viewportRect.right
        : viewportRect.left - itemRect.left;
      const endOverflow = this.isRtl
        ? viewportRect.left - itemRect.left
        : itemRect.right - viewportRect.right;

      if (startOverflow > 1) {
        this.setOffset(this.offset - startOverflow, true);
      } else if (endOverflow > 1) {
        this.setOffset(this.offset + endOverflow, true);
      }
    }

    pauseForFocus() {
      this.focusInside = true;
      this.clearAutoScroll();
    }

    resumeAfterFocus(event) {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      this.focusInside = false;
      this.syncAutoScroll();
    }

    pauseForPointer() {
      this.pointerInside = true;
      this.clearAutoScroll();
    }

    resumeAfterPointer() {
      this.pointerInside = false;
      this.syncAutoScroll();
    }

    stopAutoScroll() {
      this.userControlled = true;
      this.clearAutoScroll();
    }

    clearAutoScroll() {
      if (this.autoScrollTimer === null) return;
      window.clearTimeout(this.autoScrollTimer);
      this.autoScrollTimer = null;
    }

    syncAutoScroll() {
      this.clearAutoScroll();
      if (
        !this.isConnected ||
        this.maximumOffset <= 1 ||
        this.userControlled ||
        this.pointerInside ||
        this.focusInside ||
        this.reducedMotionQuery.matches
      ) return;

      this.autoScrollTimer = window.setTimeout(() => {
        this.autoScrollTimer = null;
        this.advanceAutoScroll();
      }, this.autoScrollDelay);
    }

    advanceAutoScroll() {
      if (this.offset >= this.maximumOffset - 1) this.autoScrollDirection = -1;
      if (this.offset <= 1) this.autoScrollDirection = 1;
      this.setOffset(this.offset + (this.getScrollDistance() * this.autoScrollDirection), true);
      this.syncAutoScroll();
    }

    finishTransition(event) {
      if (event.target !== this.track || event.propertyName !== 'transform') return;
      this.track.classList.remove('nav-links--animating');
      this.updateControls();
    }

    updateControls() {
      const atStart = this.offset <= 1;
      const atEnd = this.offset >= this.maximumOffset - 1;
      this.previousButton.disabled = atStart;
      this.nextButton.disabled = atEnd;
    }
  }

  customElements.define('header-menu-rail', HeaderMenuRail);
})();
